import ErrorIcon from '@assets/images/icons/error.svg';
import { SimpleTooltip } from '@features/ui-atoms/components/Tooltip';
import ErrorMessage from '@features/ui-form/components/ErrorMessage';
import { styled } from '@mui/joy';
import { DotPath } from '@root/src/utils/types/DotPath';
import { XOR } from '@root/src/utils/types/XOR';
import type { ReactNode } from 'react';
import React from 'react';

export type FieldWrapperProps<T = string> = XOR<
  { name: T extends string ? T : T extends object ? DotPath<T> : string },
  {
    names: Array<T extends string ? T : T extends object ? DotPath<T> : string>;
  }
> & {
  children: ReactNode;
  hideError?: boolean;
  label?: string;
  required?: boolean;
  tooltip?: string;
  /**
   * The field's error message: an i18n key, a key carrying its values
   * (`messageWithValues`), or a ready string. `''` or absent for none.
   * `FormField` passes `fieldState.error.message`; a caller outside a form
   * (a filter, a modal) passes nothing.
   */
  error?: string;
};

/**
 * Label, tooltip, required mark, inputs and error line of one field.
 *
 * Reads no form context: the error is a prop, and `FormField` — the only
 * form binding — always gives it (ADR 0001). Outside a form the wrapper is
 * plain layout. Known input children receive `error={hasError}` for the red
 * border; the message is rendered under the row.
 *
 * @param param0 Props
 * @param param0.children Children (inputs etc.)
 * @param param0.hideError Hide the error line even when `error` is set
 * @param param0.label Label for the element
 * @param param0.name Field name the label points at
 * @param param0.names Field names if there are multiple elements
 * @param param0.required Is the element required
 * @param param0.tooltip Tooltip for the element
 * @param param0.error The error message, `''` or absent for none
 */
const FieldWrapper = <T,>({
  children,
  error,
  hideError,
  label,
  name,
  names,
  required,
  tooltip,
}: FieldWrapperProps<T>) => {
  const message = hideError ? '' : (error ?? '');
  const hasError = message !== '';

  const addErrorToChildren = (c: ReactNode): ReactNode =>
    React.Children.map(c, child => {
      if (React.isValidElement(child)) {
        if (child.type === React.Fragment) {
          const processedChildren = addErrorToChildren((child.props as { children: ReactNode }).children);
          return React.cloneElement(child, {}, processedChildren);
        }

        // Only add error prop to known form components that support it
        // This prevents passing error prop to components like Button that don't support it
        const componentName = typeof child.type === 'string' ? child.type : child.type.name;
        const formComponents = ['input', 'select', 'textarea', 'Input', 'Select', 'Textarea', 'DatePickerInput'];

        if (formComponents.includes(componentName)) {
          return React.cloneElement(child, { error: hasError } as { error: boolean });
        }

        return child;
      }
      return child;
    });

  return (
    <Container>
      <LabelStyled htmlFor={name ?? names?.join(',')}>
        {label}
        {tooltip && <SimpleTooltip title={tooltip} />}
        {required && <RequiredStyled>*</RequiredStyled>}
      </LabelStyled>

      <FieldRow>{addErrorToChildren(children)}</FieldRow>

      {hasError && <ErrorMessage error={message}>{renderError}</ErrorMessage>}
    </Container>
  );
};

export default FieldWrapper;

const renderError = (msg: string): ReactNode =>
  msg && (
    <ErrorInnerContainer>
      <ErrorIcon />
      <ErrorMessageStyled>{msg}</ErrorMessageStyled>
    </ErrorInnerContainer>
  );

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '8px',
  borderRadius: '2px',
  position: 'relative',
}));

const FieldRow = styled('div')(() => ({
  display: 'flex',
}));

export const Unit = styled('div')(({ theme: { palette, typography } }) => ({
  display: 'flex',
  justifyItems: 'center',
  alignItems: 'center',
  backgroundColor: palette.grey2,
  padding: '6px 12px 6px 12px',
  ...typography['body-small'],
  color: palette.grey4,
  borderTopRightRadius: '2px',
  borderBottomRightRadius: '2px',
}));

const LabelStyled = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  ...theme.typography['small-medium'],
  color: theme.vars.palette.black,
}));

const RequiredStyled = styled('span')(({ theme }) => ({
  color: theme.palette.orange1,
}));

const ErrorInnerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.grey4,
}));

const ErrorMessageStyled = styled('span')(({ theme }) => ({
  ...theme.typography['body-small'],
  color: theme.palette.orange1,
}));
